import React, { useState } from "react";
import { MapPin, Pencil, Phone, Star, Ban, CheckCircle } from "lucide-react";
import { StoreAdminDetails } from "../../../../types/modules/admin/StoreAdmin.type";
import { formatNumber, formatPhoneNumber } from "../../../../utils/formatNumber";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { StoreStatus } from "../../../../types/enum/storeStatus";
import { useUpdateResource } from "../../../../hooks/useUpdateResource";
import { PersistGate } from "redux-persist/integration/react";
import { PermissionGate } from "../../PermissionGate";
import { PermissionsKey } from "../../../../data/permissions";

type Props = {
  store: StoreAdminDetails;
};

const BasicInfo: React.FC<Props> = ({ store }) => {
  const { averageRating, numberOfRates, city, phone ,status} = store;
  const { t } = useTranslation();

  const [isSuspend , setIsSuspend] = useState(status===StoreStatus.SUSPENDED);

   const {loading,updateResource} = useUpdateResource();
    
      const handleSuspendStatus = async () => {
        const endpoint = isSuspend ? `store/${store.id}/approved` :`store/${store.id}/suspend`;
            await  updateResource(endpoint,{}, "admin",{
                method: "PUT",
                successMessage: t("store_status_updated_successfully"),
                invalidateQueries:["store-detalis-by-admin","stores-by-admin"],
                confirmMessage:t('confirm_change_store_status')
              });
              setIsSuspend(p=>!p);
      };

  return (
    <div className="flex flex-wrap gap-4 items-center justify-between border-b border-muted pb-4 mb-4">
      {/* Left side (info) */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2 text-YellowSecondary">
          <Star className="w-5 h-5 text-YellowSecondary fill-YellowSecondary" />
          <span className="font-semibold">
            {formatNumber(averageRating)} ({formatNumber(numberOfRates)} تقييم)
          </span>
        </div>

        <div className="flex items-center gap-2 text-muted">
          <MapPin className="w-5 h-5" />
          <span>{city}</span>
        </div>

        <div className="flex items-center gap-2 text-muted">
          <Phone className="w-5 h-5" />
          <span>{formatPhoneNumber(phone)}</span>
        </div>
      </div>

      {/* Right side (Actions) */}
      <div className="flex items-center gap-3">
        {/* Suspend / Reactivate button */}
        <PermissionGate permissions={[PermissionsKey.ChangeStoreStatus]}>
          <button
          onClick={() =>handleSuspendStatus()}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-white transition ${
            isSuspend 
              ? "bg-success hover:bg-success/90"
              : "bg-danger hover:bg-danger/90"
          }`}
        >
          { isSuspend ? (
            <>
              <CheckCircle className="w-4 h-4" />
              <span>{t("reactivate")}</span>
            </>
          ) : (
            <>
              <Ban className="w-4 h-4" />
              <span>{t("suspend")}</span>
            </>
          )}
        </button>
        </PermissionGate>

        {/* Edit button */}
        <PermissionGate permissions={[PermissionsKey.UpdateStore]}>
          <Link
          to={`/admin/stores/${store.id}/settings`}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
        >
          <Pencil className="w-4 h-4" />
          <span>{t("update_information")}</span>
        </Link>
        </PermissionGate>
      </div>
       {/* Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default BasicInfo;
